import { ApiError } from "@/api/client";
import { fetchSocioEmpresas } from "@/api/socios";
import { Pagination } from "@/components/Pagination";
import { SituacaoBadge } from "@/components/SituacaoBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/ui/states";
import {
  formatCnpj,
  formatCurrencyBRL,
  formatDateBR,
  formatNumber,
  onlyDigits,
} from "@/lib/format";
import { PORTE_LABELS, UI, labelFor } from "@/lib/labels";
import { cn } from "@/lib/utils";
import type { SocioEmpresasResponse } from "@/types/api";
import { Building2, Users } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const MIN_NOME = 3;
const SKELETON_ROWS = Array.from({ length: 6 }, (_, index) => `skeleton-${index}`);
const headerCellClass = "px-3 py-2.5 font-medium";
const cellClass = "px-3 py-2.5 align-top";

/** Query string canônica da visão de sócio (`/socio?...`). */
export function socioSearchParams(nome: string, documento: string | null): string {
  const params = new URLSearchParams();
  params.set("nome", nome);
  if (documento) {
    params.set("documento", documento);
  }
  return params.toString();
}

interface SocioViewState {
  status: "idle" | "loading" | "success" | "error";
  data: SocioEmpresasResponse | null;
  error: string | null;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Erro inesperado. Tente novamente.";
}

interface SocioEmpresasProps {
  nome: string;
  documento: string | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** URL de origem dos links de CNPJ (state.from da navegação de página). */
  from: string;
  /**
   * Navegação interna (drawer): clique simples num CNPJ chama este callback em
   * vez de navegar. Cmd/Ctrl+clique segue abrindo a página completa.
   */
  onOpenCnpj?: (cnpj: string) => void;
  /** Ação exibida no estado "sócio não informado" (ex.: botão voltar). */
  invalidAction?: ReactNode;
}

/**
 * Empresas em que uma pessoa/entidade é sócia (header + tabela + paginação).
 * Reutilizado pela SocioPage (estado na URL) e pelo CnpjDrawer (estado local).
 */
export function SocioEmpresas({
  nome,
  documento,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  from,
  onOpenCnpj,
  invalidAction,
}: SocioEmpresasProps) {
  const nomeTrimmed = nome.trim();
  const doc = documento?.trim() || null;

  // PJ (documento com 14 dígitos) casa só pelo documento — vale mesmo sem nome.
  const docDigits = onlyDigits(doc);
  const isPjDoc = docDigits.length === 14;
  const valid = nomeTrimmed.length >= MIN_NOME || isPjDoc;
  const queryKey = JSON.stringify([nomeTrimmed, doc]);

  const [reloadToken, setReloadToken] = useState(0);
  const [view, setView] = useState<SocioViewState>(() => ({
    status: valid ? "loading" : "idle",
    data: null,
    error: null,
  }));
  const dataQueryKeyRef = useRef<string | null>(null);

  // Busca as empresas do sócio. Mesmo padrão robusto da pesquisa: aborta a
  // execução anterior, ignora respostas abortadas (StrictMode) e, em erro,
  // descarta resultados stale (mantém-os apenas na paginação da mesma consulta).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadToken é um nonce que força repetir a busca (retry) sem alterar a URL.
  useEffect(() => {
    if (!valid) {
      setView({ status: "idle", data: null, error: null });
      dataQueryKeyRef.current = null;
      return;
    }
    const controller = new AbortController();
    const sameQuery = dataQueryKeyRef.current === queryKey;
    setView((previous) => ({
      status: "loading",
      data: sameQuery ? previous.data : null,
      error: null,
    }));
    fetchSocioEmpresas(nomeTrimmed, doc, page, pageSize, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) {
          return;
        }
        dataQueryKeyRef.current = queryKey;
        setView({ status: "success", data: response, error: null });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        dataQueryKeyRef.current = null;
        setView({ status: "error", data: null, error: errorMessage(error) });
      });
    return () => controller.abort();
  }, [nomeTrimmed, doc, page, pageSize, valid, queryKey, reloadToken]);

  const handleRetry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const isLoading = view.status === "loading";

  // Cabeçalho: com nome, mostra o nome; sem nome (PJ só por documento), mostra o
  // documento como CNPJ e, quando os dados chegam, a razão social do 1º item.
  const firstItemName = view.data?.items[0]?.razao_social ?? null;
  const displayDoc = doc ? (isPjDoc ? formatCnpj(doc) : doc) : null;
  const displayTitle =
    nomeTrimmed.length >= MIN_NOME ? nomeTrimmed : (firstItemName ?? displayDoc ?? "—");

  return (
    <div className="space-y-5">
      <header className="rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
            <Users className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Sócio</p>
            <h1 className="text-xl font-semibold text-foreground">{displayTitle}</h1>
            <p className="text-sm text-muted-foreground">
              {displayDoc ? <span className="font-mono">{displayDoc}</span> : null}
              {view.data ? (
                <span>
                  {displayDoc ? " · " : ""}
                  <span className="font-medium text-foreground">
                    {formatNumber(view.data.total)}
                  </span>{" "}
                  {view.data.total === 1 ? "empresa" : "empresas"}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </header>

      {!valid ? (
        <EmptyState
          icon={<Users className="h-10 w-10" aria-hidden="true" />}
          title="Sócio não informado"
          description="O nome do sócio está ausente ou é muito curto (mínimo de 3 caracteres)."
          action={invalidAction}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-sm">
          {view.status === "error" ? (
            <ErrorState
              title="Não foi possível carregar"
              message={view.error ?? "Falha ao buscar as empresas do sócio."}
              onRetry={handleRetry}
            />
          ) : isLoading && !view.data ? (
            <div className="space-y-2 p-4">
              {SKELETON_ROWS.map((id) => (
                <Skeleton key={id} className="h-11 w-full" />
              ))}
            </div>
          ) : view.data && view.data.items.length === 0 ? (
            <EmptyState
              icon={<Building2 className="h-10 w-10" aria-hidden="true" />}
              title="Nenhuma empresa encontrada"
              description="Não localizamos empresas para este sócio."
            />
          ) : view.data ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[64rem] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className={headerCellClass}>{UI.table.razaoSocial}</th>
                      <th className={headerCellClass}>{UI.table.situacao}</th>
                      <th className={headerCellClass}>{UI.table.local}</th>
                      <th className={headerCellClass}>Qualificação</th>
                      <th className={headerCellClass}>Entrada</th>
                      <th className={cn(headerCellClass, "text-right")}>{UI.table.capital}</th>
                      <th className={headerCellClass}>{UI.filters.porte}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.data.items.map((item, index) => {
                      const itemCnpj = item.cnpj;
                      return (
                        <tr
                          key={`${item.cnpj_basico}-${index}`}
                          className="border-b border-border/60 transition-colors hover:bg-muted/40"
                        >
                          <td className={cellClass}>
                            {itemCnpj ? (
                              <Link
                                to={`/cnpj/${itemCnpj}`}
                                state={{ from }}
                                className="font-medium text-accent hover:underline"
                                onClick={(event) => {
                                  if (!onOpenCnpj || event.metaKey || event.ctrlKey) {
                                    return;
                                  }
                                  event.preventDefault();
                                  onOpenCnpj(itemCnpj);
                                }}
                              >
                                {item.razao_social || "—"}
                              </Link>
                            ) : (
                              <span className="font-medium text-foreground">
                                {item.razao_social || "—"}
                              </span>
                            )}
                            {item.cnpj ? (
                              <div className="font-mono text-xs text-muted-foreground">
                                {formatCnpj(item.cnpj)}
                              </div>
                            ) : null}
                          </td>
                          <td className={cellClass}>
                            <SituacaoBadge codigo={item.situacao_cadastral} />
                          </td>
                          <td className={cn(cellClass, "whitespace-nowrap")}>
                            <span className="font-medium text-foreground">{item.uf}</span>
                            <span className="text-muted-foreground"> · {item.municipio_nome}</span>
                          </td>
                          <td className={cellClass}>{item.qualificacao_descricao ?? "—"}</td>
                          <td className={cn(cellClass, "whitespace-nowrap")}>
                            {formatDateBR(item.data_entrada)}
                          </td>
                          <td className={cn(cellClass, "whitespace-nowrap text-right")}>
                            {formatCurrencyBRL(item.capital_social)}
                          </td>
                          <td className={cellClass}>{labelFor(PORTE_LABELS, item.porte)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                total={view.data.total}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                disabled={isLoading}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
